import { datasFases } from '../lib/fasesDatas';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../supabase';
import { segmentosRota, irPara, useAoVoltar } from '../lib/rota';
import VendasView from './VendasView';
import ObjetivosView from './ObjetivosView';
import DashboardView from './DashboardView';

import { TEMPLATES, temFollowups } from './KanbanPage';
import { encontrarCliente } from '../lib/nomesClientes';
import { ehEvento, tipoEventoDe, eventoAtivo, sincronizarReceita } from '../lib/eventos';
import { ehFollowup, criarFollowups } from '../lib/followups';
import { iniciarPassagem } from '../lib/passagem';
import CommissionsView from './CommissionsView';
import MarketingPage from './MarketingPage';

const C = {
  purple:"#691FB1", purpleLight:"#EDE7F6", purpleDark:"#736CCE",
  pink:"#FF357B", pinkLight:"#FFF0F5",
  green:"#27AE60", greenLight:"#EAFAF1",
  amber:"#F39C12", amberLight:"#FEF9E7",
  red:"#E74C3C", redLight:"#FDEDEC",
  blue:"#57B1DD", blueLight:"#E5F7FF",
  bg:"#F7F5FC", white:"#FFFFFF",
  text:"#1A1A2E", textMid:"#4A4A6A", textLight:"#8888AA", border:"#E4DEFA",
};

const ESTADOS = {
  rascunho:    { label:"Rascunho",          color:C.textLight, bg:C.bg },
  revisao:     { label:"Revisão operac.",   color:C.amber,     bg:C.amberLight },
  financeira:  { label:"Aprov. financeira", color:C.blue,      bg:C.blueLight },
  aprovado:    { label:"Aprovado interno",  color:C.purpleDark,bg:C.purpleLight },
  enviado:     { label:"Enviado",           color:C.purple,    bg:C.purpleLight },
  ganho:       { label:"Ganho",             color:C.green,     bg:C.greenLight },
  perdido:     { label:"Perdido",           color:C.red,       bg:C.redLight },
};
const ABERTOS = ['rascunho','revisao','financeira','aprovado','enviado'];
const TIPOS_NEGOCIO = ['Novo','Retenção','Upsell','Cross'];
const BILLING_MODELS = ['Único','Mensal fixo','Pré-pago','Por sessão'];
const resumoItens = (itens) => (itens||[]).map(it=>{
  const preco = (it.preco_unit||0)*(1-(it.desconto_pct||0)/100);
  return `• ${it.descricao||'(item)'} — ${it.qtd||0}x a ${(preco).toFixed(2)}€`;
}).join('\n');
import { LINHAS } from '../constants/linhas';
import { calcularWow, PLANO_POR_CODIGO, AREAS_PADRAO, ADDON_AREA, MINIMO_MENSAL } from '../lib/calculadoraWow';

const MARGEM_MIN = 35, MARGEM_CRIT = 30;
const eur = n => (n||0).toLocaleString("pt-PT",{minimumFractionDigits:0,maximumFractionDigits:0})+"€";
const eur2 = n => (n||0).toLocaleString("pt-PT",{minimumFractionDigits:2,maximumFractionDigits:2})+"€";
const pct = n => (n||0).toFixed(0)+"%";
// linhas de um orçamento: a lista nova, ou a linha antiga quando só há essa
const linhasDe = o => (Array.isArray(o?.linhas) && o.linhas.length) ? o.linhas : (o?.linha ? [o.linha] : []);
const margemCor = m => m<MARGEM_CRIT ? C.red : m<MARGEM_MIN ? C.amber : C.green;

const CUSTOS_ADIC = [
  ['custo_profissional','Profissional'],
  ['custo_deslocacao','Deslocação'],
  ['custo_alojamento','Alojamento'],
  ['custo_materiais','Materiais'],
  ['custo_espaco','Espaço'],
  ['custo_producao','Produção'],
  ['custo_catering','Catering'],
  ['custo_licencas','Licenças'],
  ['custo_outros','Outros'],
];

function calcOrc(itens, orc){
  let semIva=0, iva=0, custoItens=0, descTot=0;
  (itens||[]).forEach(it=>{
    const bruto = (it.preco_unit||0) * (it.qtd||0);
    const precoFinal = (it.preco_unit||0) * (1-(it.desconto_pct||0)/100);
    const sub = precoFinal * (it.qtd||0);
    semIva += sub;
    descTot += bruto - sub;
    iva += sub * ((it.iva||23)/100);
    custoItens += (it.custo_unit||0) * (it.qtd||0);
  });
  const custosAdic = CUSTOS_ADIC.reduce((a,[k])=>a+(Number(orc?.[k])||0),0);
  const custoDir = custoItens + custosAdic;
  const margemEur = semIva - custoDir;
  const margemPct = semIva>0 ? (margemEur/semIva)*100 : 0;
  return { semIva, iva, comIva:semIva+iva, custoDir, custosAdic, margemEur, margemPct, descTot };
}

export default function CommercialPage({ perms, userName, userEmail, vistaInicial }){
  const [view, setView] = useState(vistaInicial || 'list');
  const [posGanho, setPosGanho] = useState(null);   // janela depois de adjudicar
  const [aEnviarForm, setAEnviarForm] = useState(false);
  const [emailForm, setEmailForm] = useState('');

  // Pedido de dados de faturação ao cliente. Também existe na ficha do cliente,
  // para quem não enviar na altura ou precisar de reenviar.
  const pedirDadosFaturacao = async (g, destino) => {
    if (!g.cliente_id) {
      alert('Este orçamento não está ligado a um cliente do HUB.\nLiga-o primeiro e depois envia o pedido na ficha do cliente.');
      return false;
    }
    const dest = (destino || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(dest)) { alert('Esse email não parece válido.'); return false; }

    const codigo = 'WW-' + Math.random().toString(36).slice(2,10).toUpperCase();
    const { error } = await supabase.from('formularios_cliente').insert({
      cliente_id: g.cliente_id, projeto_id: g.projeto_id, orcamento_id: g.orcamento_id,
      codigo, email_envio: dest, enviado_por: userEmail || null,
    });
    if (error) { alert('Não consegui criar o pedido: ' + error.message); return false; }

    const link = `https://hub.workwell.pt/formulario-cliente?c=${codigo}`;
    try {
      await supabase.functions.invoke('Send-email', { body: {
        to: dest, toName: '', fromName: 'Workwell',
        subject: `Workwell — dados para faturação (${g.cliente})`,
        html: `<div style="font-family:Helvetica,Arial,sans-serif;max-width:540px;margin:0 auto;padding:24px;color:#1A1A2E">
<div style="background:#691FB1;border-radius:12px 12px 0 0;padding:18px 24px">
  <span style="color:#fff;font-size:17px;font-weight:700">Dados para faturação</span></div>
<div style="border:1px solid #E4DEFA;border-top:none;border-radius:0 0 12px 12px;padding:24px">
  <p style="font-size:14.5px;line-height:1.65;margin:0 0 14px">Olá,</p>
  <p style="font-size:14.5px;line-height:1.65;margin:0 0 14px">
    Para darmos início ao programa, precisamos dos dados de faturação e de saber
    com quem falar do vosso lado. Demora dois minutos.</p>
  <div style="text-align:center;margin:24px 0">
    <a href="${link}" style="display:inline-block;background:#691FB1;color:#fff;text-decoration:none;
      border-radius:9px;padding:14px 30px;font-size:15px;font-weight:700">Preencher os dados</a></div>
  <p style="font-size:12.5px;color:#8888AA;line-height:1.6;margin:0">
    Se o botão não funcionar, copie este endereço:<br/>
    <span style="color:#691FB1;word-break:break-all">${link}</span></p>
  <p style="font-size:12.5px;color:#8888AA;margin:18px 0 0">
    Qualquer questão: <strong>financeiro@workwell.pt</strong></p>
</div></div>`,
      }});
      return true;
    } catch(e) {
      alert('O pedido foi criado mas o email falhou. O link é:\n\n' + link);
      return true;
    }
  };
  useEffect(() => { if (vistaInicial) setView(vistaInicial); }, [vistaInicial]);
  const [orcamentos, setOrcamentos] = useState([]);
  const [itensPorOrc, setItensPorOrc] = useState({});
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroLinha, setFiltroLinha] = useState("");
  const [filtroComercial, setFiltroComercial] = useState("");
  const [busca, setBusca] = useState("");
  const [filtroPeriodo, setFiltroPeriodo] = useState("tudo"); // tudo|mes|trimestre|ano
  const [draft, setDraft] = useState(null);
  // briefing da passagem às Operações: pedido ao marcar Ganho (fica guardado em cada projeto criado)
  const [briefingAberto, setBriefingAberto] = useState(null);
  const briefingRef = useRef(null);
  const [projetosList, setProjetosList] = useState([]);
  const [draftItens, setDraftItens] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCustos, setShowCustos] = useState(false);
  const [clientesN, setClientesN] = useState([]);
  const [leadsList, setLeadsList] = useState([]);
  const [buscaLead, setBuscaLead] = useState('');
  const [novoCliente, setNovoCliente] = useState('');

  useEffect(()=>{ load(); },[]);
  // endereço: /comercial/orcamento/<id>. O link que vai para o Zoho continua a ser ?orc=<id> (as funções do Zoho leem-no).
  const orcDaRota = () => { const s = segmentosRota(); return s[0] === 'comercial' && s[1] === 'orcamento' && s[2] ? s[2] : null; };
  const orcPendente = useRef(new URLSearchParams(window.location.search).get('orc') || orcDaRota());
  useEffect(()=>{
    const orcId = orcPendente.current;
    if (orcId && orcamentos.length) {
      orcPendente.current = null;
      const o = orcamentos.find(x => String(x.id) === String(orcId));
      if (o) abrirEdicao(o); else irPara('/comercial', { modulo:'comercial', substituir:true });
    }
  }, [orcamentos]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    if (orcPendente.current) return;
    irPara(draft && draft.id ? `/comercial/orcamento/${draft.id}` : '/comercial', { modulo:'comercial' });
  }, [draft && draft.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useAoVoltar(() => { if (segmentosRota()[0] !== 'comercial') return; const id = orcDaRota();
    if (!id) setDraft(null); else if (!draft || String(draft.id) !== id) { const o = orcamentos.find(x => String(x.id) === id); if (o) abrirEdicao(o); } });
  const load = async () => {
    setLoading(true);
    const [{ data:orcs }, { data:itens }, { data:servs }, { data:cls }, { data:projs }] = await Promise.all([
      supabase.from('orcamentos').select('*').eq('active',true).order('created_at',{ascending:false}).limit(2000),
      supabase.from('orc_itens').select('*').limit(5000),
      supabase.from('orc_servicos').select('*').eq('ativo',true).order('codigo'),
      supabase.from('clientes').select('id,nome').order('nome'),
      supabase.from('projects').select('id,name,client,cliente_id,active,status').eq('active',true).order('name').limit(3000),
    ]);
    setProjetosList(projs||[]);
    setClientesN(cls||[]);
    const map={};
    (itens||[]).forEach(it=>{ (map[it.orcamento_id] = map[it.orcamento_id]||[]).push(it); });
    setItensPorOrc(map); setOrcamentos(orcs||[]); setServicos(servs||[]);
    const { data:_lds } = await supabase.from('leads').select('id,data,empresa,pagina').order('data',{ascending:false}).limit(5000);
    if(_lds) setLeadsList(_lds);
    setLoading(false);
  };

  const comCalc = useMemo(()=> (orcamentos||[]).map(o=>({ ...o, calc:calcOrc(itensPorOrc[o.id], o) })), [orcamentos, itensPorOrc]);
  const dentroPeriodo = (dataStr)=>{
    if(filtroPeriodo==="tudo") return true;
    if(!dataStr) return false;
    const d=new Date(dataStr), hoje=new Date();
    if(filtroPeriodo==="mes") return d.getFullYear()===hoje.getFullYear() && d.getMonth()===hoje.getMonth();
    if(filtroPeriodo==="ano") return d.getFullYear()===hoje.getFullYear();
    if(filtroPeriodo==="trimestre"){ const tq=Math.floor(hoje.getMonth()/3); return d.getFullYear()===hoje.getFullYear() && Math.floor(d.getMonth()/3)===tq; }
    return true;
  };
  const comerciais = useMemo(()=> {
    const set = new Set();
    (orcamentos||[]).forEach(o=>{ if(o.comercial_nome && o.comercial_nome.trim()) set.add(o.comercial_nome.trim()); });
    return Array.from(set).sort((a,b)=>a.localeCompare(b));
  }, [orcamentos]);

  const filtrados = useMemo(()=> comCalc.filter(o=>{
    // visibilidade: comercial ve so os seus; admin/financial veem todos
    if(!perms?.canViewAllCommercial && (o.comercial_email||'').toLowerCase() !== (userEmail||'').toLowerCase()) return false;
    if(filtroEstado && o.estado!==filtroEstado) return false;
    if(filtroLinha && !linhasDe(o).includes(filtroLinha)) return false;
    if(filtroComercial && (o.comercial_nome||'').trim()!==filtroComercial) return false;
    if(busca && !((o.cliente||"").toLowerCase().includes(busca.toLowerCase()) || (o.numero||"").toLowerCase().includes(busca.toLowerCase()))) return false;
    if(!dentroPeriodo(o.data_decisao)) return false;
    return true;
  }), [comCalc, filtroEstado, filtroLinha, filtroComercial, busca, filtroPeriodo, perms, userEmail]);

  const kpis = useMemo(()=>{
    const abertos = filtrados.filter(o=>ABERTOS.includes(o.estado));
    const ganhos = filtrados.filter(o=>o.estado==='ganho');
    const fechados = filtrados.filter(o=>o.estado==='ganho'||o.estado==='perdido');
    const pipeline = abertos.reduce((a,o)=>a+o.calc.semIva,0);
    const adjudicado = ganhos.reduce((a,o)=>a+o.calc.semIva,0);
    const comMargem = filtrados.filter(o=>o.calc.semIva>0);
    const margemMedia = comMargem.length ? comMargem.reduce((a,o)=>a+o.calc.margemPct,0)/comMargem.length : 0;
    const conversao = fechados.length ? (ganhos.length/fechados.length)*100 : 0;
    const pendentes = abertos.length;
    return { pipeline, adjudicado, margemMedia, conversao, pendentes };
  }, [filtrados]);

  const novoNumero = () => '';  // numero atribuido automaticamente pela base de dados ao guardar
  const abrirNovo = () => {
    setDraft({ numero:novoNumero(), cliente:'', nif:'', n_colaboradores:'', contacto:'', email:'', telefone:'',
      comercial_nome:userName||'', comercial_email:userEmail||'', linha:'', linhas:[], validade:'', estado:'rascunho', descricao:'', condicoes:'', obs_internas:'',
      ...Object.fromEntries(CUSTOS_ADIC.map(([k])=>[k,0])) });
    setDraftItens([]); setShowCustos(false); setView('edit');
  };
  const abrirEdicao = (o) => {
    setDraft({ ...o, _estadoOriginal:o.estado });
    setDraftItens((itensPorOrc[o.id]||[]).map(it=>({ ...it })));
    setShowCustos(CUSTOS_ADIC.some(([k])=>Number(o[k])>0));
    setView('edit');
  };

  const juntarLinha = (l) => { if(!l) return; setDraft(d => { const arr = linhasDe(d); return arr.includes(l) ? d : { ...d, linhas:[...arr, l], linha: arr[0] || l }; }); };
  const addServico = (servId) => {
    const s = servicos.find(x=>x.id===servId);
    if(!s) return;
    juntarLinha(s.linha);
    const novo = { _tmp:Math.random().toString(36).slice(2), servico_id:s.id, descricao:s.nome,
      qtd:1, preco_unit:Number(s.preco_base)||0, desconto_pct:0, iva:(s.iva==null||s.iva==='')?23:Number(s.iva), custo_unit:Number(s.custo_padrao)||0, unidade:s.unidade||'', notas:'' };
    // Care 3 · Care 6 · Flex: já entra com o valor da calculadora WoW (12 meses)
    const plano = PLANO_POR_CODIGO[s.codigo];
    if (plano) {
      const calculo = { n: draft.n_colaboradores || '', areas: AREAS_PADRAO, addon: ADDON_AREA, excluiPsicologia: false, minimo: MINIMO_MENSAL, meses: 12, plano };
      const r = calcularWow(calculo);
      Object.assign(novo, { calculo, qtd: 12, unidade: 'mês' }, r ? { preco_unit: Math.round(r.totalMensal * 100) / 100, custo_unit: Math.round(r.custoMensal * 100) / 100 } : {});
    }
    setDraftItens(arr=>[...arr, novo]);
  };
  const addLivre = () => setDraftItens(arr=>[...arr, { _tmp:Math.random().toString(36).slice(2), servico_id:null, descricao:'', qtd:1, preco_unit:0, desconto_pct:0, iva:23, custo_unit:0, unidade:'', notas:'' }]);
  const updItem = (idx, patch) => setDraftItens(arr=>arr.map((it,i)=>i===idx?{...it,...patch}:it));
  const delItem = (idx) => setDraftItens(arr=>arr.filter((_,i)=>i!==idx));
  const calc = useMemo(()=>calcOrc(draftItens, draft), [draftItens, draft]);

  const apagar = async () => {
    if(!draft.id){ alert('Este orçamento ainda não foi guardado.'); return; }
    const temProjeto = !!draft.projeto_id;
    let apagarProjeto = false;
    if(temProjeto){
      const r = window.confirm('Este orçamento já gerou um projeto.\n\nAtenção: o registo da venda associada também será apagado.\n\nOK = apagar o orçamento, a venda E o projeto (e as suas fases).\nCancelar = apagar SÓ o orçamento e a venda (o projeto fica).');
      apagarProjeto = r;
    } else {
      if(!window.confirm('Apagar este orçamento?\n\nAtenção: o registo da venda associada também será apagado.\n\nEsta ação é definitiva.')) return;
    }
    setSaving(true);
    if(apagarProjeto && draft.projeto_id){
      await supabase.from('project_phases').delete().eq('project_id',draft.projeto_id);
      await supabase.from('projects').delete().eq('id',draft.projeto_id);
    }
    await supabase.from('vendas').delete().eq('orcamento_id',draft.id);
    await supabase.from('orc_itens').delete().eq('orcamento_id',draft.id);
    await supabase.from('orcamentos').delete().eq('id',draft.id);
    setSaving(false);
    await load();
    setView('list');
  };

  const guardar = async () => {
    if(!draft.cliente){ alert('Indica o cliente.'); return; }
    // ao ganhar, cada item tem de ter um serviço da biblioteca (código de faturação):
    // é por ele que o projeto é criado e que o Financeiro soma faturação e margem
    if(draft.estado==='ganho' && draft._estadoOriginal!=='ganho'){
      const semCodigo = draftItens.filter(it => !it.servico_id);
      if(!draftItens.length){ alert('Antes de marcar Ganho, acrescenta os itens do orçamento.'); return; }
      if(semCodigo.length){
        alert('Antes de marcar Ganho, escolhe o serviço da biblioteca em cada item.\n\nSem serviço: ' + semCodigo.map(it => it.descricao || '(item sem descrição)').join(', '));
        return;
      }
      // passagem às Operações: sem briefing não se marca ganho
      if(!briefingRef.current){
        const dt = (x) => x ? x.split('-').reverse().join('/') : '';
        setBriefingAberto({
          renovacao: draft.tipo_negocio === 'Retenção',
          contexto: draft.descricao || '',
          contactos: [draft.contacto, draft.email, draft.telefone].filter(Boolean).join(' · '),
          acessos: '',
          datas_chave: [draft.contract_start && `Início ${dt(draft.contract_start)}`, draft.contract_end && `Fim ${dt(draft.contract_end)}`].filter(Boolean).join(' · '),
          notas: draft.obs_internas || '',
        });
        return;
      }
    }
    setSaving(true);
    // quando abre a janela do ganho, fica-se no orçamento até ela fechar — se voltasse
    // logo à lista, a janela (que só existe nesta vista) nunca chegava a aparecer
    let abriuPosGanho = false;
    const payload = {
      numero:draft.numero, cliente:draft.cliente, cliente_id:draft.cliente_id||null, zoho_deal_id:draft.zoho_deal_id||null, nif:draft.nif||null,
      n_colaboradores:draft.n_colaboradores?Number(draft.n_colaboradores):null,
      contacto:draft.contacto||null, email:draft.email||null, telefone:draft.telefone||null,
      comercial_nome:draft.comercial_nome||null, comercial_email:draft.comercial_email||null, linha:(linhasDe(draft)[0])||null, linhas:linhasDe(draft),
      comercial_origem:draft.comercial_origem||null, mes_venda:draft.mes_venda||null, equipa_envolvida:draft.equipa_envolvida||null,
      lead_id:draft.lead_id||null,
      projeto_id:draft.projeto_id||null,
      validade:draft.validade||null, estado:draft.estado||'rascunho',
      descricao:draft.descricao||null, condicoes:draft.condicoes||null, obs_internas:draft.obs_internas||null,
      ...Object.fromEntries(CUSTOS_ADIC.map(([k])=>[k, Number(draft[k])||0])),
      updated_at:new Date().toISOString(),
    };
    payload.tipo_negocio = draft.tipo_negocio||'Novo';
    if(draft.estado==='ganho' && !draft.mes_venda){ payload.mes_venda = new Date().toISOString().slice(0,7); }
    if(draft.estado==='ganho' && !draft.data_decisao){ payload.data_decisao = new Date().toISOString().slice(0,10); }
    payload.billing_model = draft.billing_model||'Único';
    payload.contract_start = draft.contract_start||null;
    payload.contract_end = draft.contract_end||null;
    let orcId = draft.id;
    if(orcId){
      await supabase.from('orcamentos').update(payload).eq('id',orcId);
      await supabase.from('orc_itens').delete().eq('orcamento_id',orcId);
    } else {
      const { data:ins } = await supabase.from('orcamentos').insert(payload).select('id').single();
      orcId = ins?.id;
    }
    if(orcId && draftItens.length){
      const rows = draftItens.map((it,i)=>({ orcamento_id:orcId, servico_id:it.servico_id||null, descricao:it.descricao||'',
        qtd:Number(it.qtd)||0, preco_unit:Number(it.preco_unit)||0, desconto_pct:Number(it.desconto_pct)||0,
        iva:(it.iva==null||it.iva==='')?23:Number(it.iva), custo_unit:Number(it.custo_unit)||0, unidade:it.unidade||null, notas:it.notas||null, ordem:i,
        billing_model:it.billing_model||null, calculo:it.calculo||null }));
      await supabase.from('orc_itens').insert(rows);
    }

    // ===== CONVERSÃO EM PROJETO (ao marcar Ganho) =====
    // Um projeto por serviço da biblioteca (código). Com um só serviço: um projeto
    // "Cliente — Serviço". Com vários: o programa-mãe do ano do cliente e um
    // subprojeto por serviço, cada um com o seu valor e tipo de faturação — é o que
    // permite ver no fim do ano o que faturou e que margem deu cada serviço.
    const criarProjetosDoGanho = async (maeExistente) => {
      const agora = new Date().toISOString();
      // eventos do Marketing: as linhas dos Wellbeing Games / Awards viram projetos desse tipo, ligados à edição ativa
      let evsMkt = [];
      try { const { data } = await supabase.from('marketing_projetos').select('id,nome,estado,ativo,created_at').eq('ativo', true); evsMkt = data || []; } catch (e) { evsMkt = []; }
      const grupos = [];
      draftItens.forEach(it => {
        const s = servicos.find(x => x.id === it.servico_id) || null;
        const chave = it.servico_id || ('livre:' + (it.descricao || ''));
        let g = grupos.find(x => x.chave === chave);
        if (!g) { g = { chave, serv: s, itens: [], modelo: it.billing_model || draft.billing_model || 'Único' }; grupos.push(g); }
        g.itens.push(it);
      });
      // nº de meses do contrato (mesma conta do Financeiro), para o mensal fixo
      const meses = (() => {
        if (!draft.contract_start || !draft.contract_end) return 0;
        const s = new Date(draft.contract_start), e = new Date(draft.contract_end);
        e.setDate(e.getDate() - 1);
        return Math.max(0, (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1);
      })();
      const base = {
        client: draft.cliente, cliente_id: draft.cliente_id || null, tipo_negocio: draft.tipo_negocio || 'Novo',
        contract_start: draft.contract_start || null, contract_end: draft.contract_end || null,
        commercial_email: draft.comercial_email || null, commercial_name: draft.comercial_nome || null,
        status: 'Ativo', active: true, project_type: 'Standard', created_at: agora, updated_at: agora,
      };
      const projetos = grupos.map(g => {
        const total = Math.round(calcOrc(g.itens, {}).semIva * 100) / 100;
        let value = total, aviso = '';
        if (g.modelo === 'Por sessão') value = 0;
        else if (g.modelo === 'Mensal fixo') {
          if (meses > 0) { value = Math.round(total / meses * 100) / 100; aviso = ` Mensal fixo: ${total.toFixed(2)}€ ÷ ${meses} meses = ${value.toFixed(2)}€/mês.`; }
          else aviso = ' ⚠ Mensal fixo sem datas de contrato: confirmar o valor MENSAL.';
        }
        const nomeServ = (g.serv && g.serv.nome) || (g.itens[0] && g.itens[0].descricao) || draft.linha || 'Serviço';
        const linha = (g.serv && g.serv.linha) || draft.linha || null;
        const tipoEv = tipoEventoDe(`${nomeServ} ${(g.itens||[]).map(i => i.descricao || '').join(' ')}`);
        const evAtivo = tipoEv ? eventoAtivo(evsMkt, tipoEv) : null;
        return { total, row: { ...base,
          ...(tipoEv ? { project_type: tipoEv, marketing_projeto_id: evAtivo ? evAtivo.id : null } : {}),
          name: `${draft.cliente} — ${nomeServ}`, value, billing_model: g.modelo,
          business_area: linha, business_areas: linha ? [linha] : [],
          servico_codigo: (g.serv && g.serv.codigo) || null,
          // o mês da venda não é o mês de faturação: nos únicos fica vazio (= mês do serviço) e escolhe-se no projeto
          billing_month: g.modelo === 'Pré-pago' ? (draft.mes_venda || null) : null,
          description: resumoItens(g.itens) || null,
          notes: `Origem: orçamento ${draft.numero}${g.serv ? ' · ' + g.serv.codigo : ''}. Valor no orçamento: ${total.toFixed(2)}€ (s/IVA).${aviso}`,
        } };
      });
      let mae = maeExistente || null;
      if (projetos.length > 1 && !mae) {
        const ano = (draft.contract_start || agora).slice(0, 4);
        // reutiliza o programa-mãe deste cliente para o ano, se já existir
        let q = supabase.from('projects').select('id,name').eq('is_mae', true).eq('active', true).ilike('name', `%${ano}%`);
        q = draft.cliente_id ? q.eq('cliente_id', draft.cliente_id) : q.eq('client', draft.cliente);
        const { data: maes } = await q.limit(1);
        if (maes && maes[0]) mae = maes[0];
        else {
          const linhas = [...new Set(projetos.map(p => p.row.business_area).filter(Boolean))];
          const { data: m, error: em } = await supabase.from('projects').insert({ ...base,
            name: `${draft.cliente} — Programa ${ano}`, is_mae: true, value: 0, billing_model: null,
            business_area: linhas[0] || null, business_areas: linhas,
            notes: `Programa-mãe criado a partir do orçamento ${draft.numero}.`,
          }).select('id,name').single();
          if (em) return { erro: em.message, criados: [] };
          mae = m;
        }
      }
      const criados = [];
      for (const p of projetos) {
        const row = mae ? { ...p.row, parent_id: mae.id } : p.row;
        const { data, error } = await supabase.from('projects').insert(row).select('id,name').single();
        if (error) return { erro: error.message, mae, criados };
        criados.push({ ...data, contract_start: row.contract_start, contract_end: row.contract_end, project_type: row.project_type });
        if (ehEvento(row.project_type)) await sincronizarReceita(supabase, { ...row, id: data.id });
      }
      return { mae, criados };
    };

    if(orcId && draft.estado==='ganho' && !draft.projeto_id){
      const c = calcOrc(draftItens, draft);
      const res = await criarProjetosDoGanho(null);
      const proj = res.mae || res.criados[0] || null;
      const perr = res.erro ? { message: res.erro } : null;
      const nomeProj = proj ? proj.name : draft.cliente;
      if(perr && res.criados.length) alert('Foram criados ' + res.criados.length + ' projeto(s), mas um falhou: ' + perr.message + '\nConfirma nos Projetos.');
      if(proj?.id){
        await supabase.from('orcamentos').update({ projeto_id:proj.id }).eq('id',orcId);
        // passagem às Operações: cada projeto criado fica à espera de ser aceite (com o briefing do comercial)
        for (const pc of res.criados) { try { await iniciarPassagem(pc.id, briefingRef.current); } catch (e) { console.error('passagem:', e); } }
        // descobrir o deal no Zoho pelo link (com retries pela indexacao), se ainda nao ligado
        let dealId = draft.zoho_deal_id;
        const orcUrl = 'https://workwell-app.vercel.app/?orc=' + orcId;
        if (!dealId) {
          for (let i = 0; i < 8 && !dealId; i++) {
            if (i > 0) await new Promise(r => setTimeout(r, 5000));
            try {
              const { data } = await supabase.functions.invoke('find-deal-by-orc', { body: { orc_url: orcUrl } });
              if (data && data.found && data.deal_id) {
                dealId = data.deal_id;
                await supabase.from('orcamentos').update({ zoho_deal_id: dealId }).eq('id', orcId);
              }
            } catch(e) { console.error('find-deal-by-orc:', e); }
          }
        }
        if (dealId) {
          try {
            await supabase.functions.invoke('sync-zoho-deal', { body: {
              zoho_deal_id: dealId,
              valor: Math.round(c.semIva*100)/100,
              orc_url: orcUrl,
              orc_id: orcId,
            }});
          } catch(e) { console.error('Zoho sync (nao bloqueia):', e); }
        }
        // criar as fases do projeto a partir do TEMPLATE do tipo (igual ao Kanban)
        // (a mãe não leva fases — só os projetos de cada serviço)
        const tmpl = (TEMPLATES['Standard'] || []).filter(t => !ehFollowup(t.nome));
        if(tmpl.length){
          for (const pc of res.criados) {
            if (ehEvento(pc.project_type)) continue;   // eventos: sem fases nem follow-ups automáticos
            const dts = datasFases(tmpl, pc.contract_start, pc.contract_end);
            const fases = tmpl.map((t,i)=>{
              return { project_id:pc.id, name:t.nome, order_num:i+1, state:'Por iniciar',
                start_date:dts[i].start, end_date:dts[i].end,
                subtasks:JSON.stringify((t.subatividades||[]).map(x=>({name:x,state:'Pendente',type:'Reunião',notes:''}))) };
            });
            await supabase.from('project_phases').insert(fases);
            if (temFollowups('Standard')) await criarFollowups(supabase, pc.id);
          }
        }
        // notificar admins + TLs do novo projeto (nao bloqueia)
        try {
          await supabase.functions.invoke('notify-new-project', { body: {
            projeto: nomeProj, cliente: draft.cliente, linha: linhasDe(draft).join(' · '),
            comercial: draft.comercial_nome||'', valor: Math.round(c.semIva*100)/100,
            billing_model: draft.billing_model||'', numero: draft.numero||'',
            inicio: draft.contract_start||null,
          }});
        } catch(e) { console.error('notify-new-project (nao bloqueia):', e); }
        // Em vez do aviso do browser, uma janela própria: o "OK / Cancelar"
        // não diz o que faz cada botão, e quem está distraído carrega no
        // primeiro. Aqui os botões dizem exatamente o que acontece.
        abriuPosGanho = true;
        setPosGanho({
          numero: draft.numero,
          projeto: nomeProj,
          cliente: draft.cliente,
          cliente_id: draft.cliente_id || null,
          valor: c.semIva,
          modelo: res.criados.length > 1
            ? `${res.criados.length} projetos, um por serviço, em ${nomeProj}`
            : ((draftItens[0] && draftItens[0].billing_model) || draft.billing_model || 'Único'),
          fases: tmpl.length,
          zoho: !!dealId,
          projeto_id: proj.id,
          orcamento_id: orcId,
          email: (draft.email||'').trim(),
        });
        setEmailForm((draft.email||'').trim());
      } else if(perr){
        alert('Orçamento guardado, mas falhou criar o projeto: '+(perr.message||''));
      }
    } else if(orcId && draft.projeto_id && draft.estado==='ganho' && draft._estadoOriginal!=='ganho'){
      // Só sincroniza o projeto quando o orçamento ACABA de transitar para ganho.
      // Se já estava ganho, NÃO se toca no projeto — preserva notas, tarefas e valores editados à mão.
      const c2 = calcOrc(draftItens, draft);
      const { data: assoc } = await supabase.from('projects').select('id,name,is_mae').eq('id', draft.projeto_id).single();
      if (assoc && assoc.is_mae) {
        // associado a um programa-mãe: cria um subprojeto por serviço dentro dele (a mãe não muda)
        const resM = await criarProjetosDoGanho({ id: assoc.id, name: assoc.name });
        if (resM.erro) alert('Falhou criar os projetos dentro de ' + assoc.name + ': ' + resM.erro);
        for (const pc of (resM.criados || [])) { try { await iniciarPassagem(pc.id, briefingRef.current); } catch (e) { console.error('passagem:', e); } }
      } else {
        const porSessao2 = (draft.billing_model==='Por sessão');
        const idsServ = [...new Set(draftItens.map(it => it.servico_id).filter(Boolean))];
        const serv1 = idsServ.length === 1 ? servicos.find(x => x.id === idsServ[0]) : null;
        const nomeProj2 = serv1 ? `${draft.cliente} — ${serv1.nome}` : ((assoc && assoc.name) || draft.cliente);
        await supabase.from('projects').update({
          name:nomeProj2, client:draft.cliente, cliente_id:draft.cliente_id||null, value:porSessao2?0:Math.round(c2.semIva*100)/100,
          billing_model:draft.billing_model||'Único', business_area:(serv1 && serv1.linha) || draft.linha||null,
          servico_codigo: serv1 ? serv1.codigo : null,
          contract_start:draft.contract_start||null, contract_end:draft.contract_end||null,
          commercial_email:draft.comercial_email||null, commercial_name:draft.comercial_nome||null,
          tipo_negocio:draft.tipo_negocio||'Novo',
          description:resumoItens(draftItens)||null,
          notes:'Origem: orçamento '+draft.numero+'. Valor estimado: '+c2.semIva.toFixed(2)+'€ (s/IVA).',
          updated_at:new Date().toISOString(),
        }).eq('id',draft.projeto_id);
        // projeto existente sem responsável nas Operações: segue a passagem; se já tem dono, não se mexe
        try { const { data: px } = await supabase.from('projects').select('owner_email,passagem_estado').eq('id', draft.projeto_id).maybeSingle();
          if (px && !px.owner_email && !px.passagem_estado) await iniciarPassagem(draft.projeto_id, briefingRef.current); } catch (e) { console.error('passagem:', e); }
        if (idsServ.length > 1) alert('Este orçamento tem ' + idsServ.length + ' serviços, mas está associado a um só projeto (não a um programa-mãe).\nPara ver a faturação por serviço, associa-o ao programa-mãe do cliente — o HUB cria um projeto por serviço.');
      }
      // se ganho, garantir ligacao e sincronizacao com o Zoho (com aviso se falhar)
      if (draft.estado==='ganho') {
        let dealId = draft.zoho_deal_id || null;
        let zohoLigado = false;
        const orcUrl = 'https://workwell-app.vercel.app/?orc=' + orcId;
        // se ja tem deal no draft mas pode nao estar gravado, garante que fica gravado
        if (dealId) {
          await supabase.from('orcamentos').update({ zoho_deal_id: dealId }).eq('id', orcId);
        } else {
          // procurar o deal no Zoho pelo link (com retries pela indexacao)
          for (let i = 0; i < 8 && !dealId; i++) {
            if (i > 0) await new Promise(r => setTimeout(r, 5000));
            try {
              const { data } = await supabase.functions.invoke('find-deal-by-orc', { body: { orc_url: orcUrl } });
              if (data && data.found && data.deal_id) {
                dealId = data.deal_id;
                await supabase.from('orcamentos').update({ zoho_deal_id: dealId }).eq('id', orcId);
              }
            } catch(e) { console.error('find-deal-by-orc:', e); }
          }
        }
        if (dealId) {
          try {
            const { data: sd } = await supabase.functions.invoke('sync-zoho-deal', { body: { zoho_deal_id: dealId, valor: Math.round(c2.semIva*100)/100, orc_url: orcUrl, orc_id: orcId } });
            zohoLigado = !!(sd && sd.ok);
          } catch(e) { console.error('Zoho sync:', e); }
        }
        if (!zohoLigado) {
          alert('Orçamento marcado como ganho, mas NÃO sincronizou com o Zoho CRM.\n\nConfirma que o campo "Orçamento_HUB" está preenchido no Deal do Zoho com este link:\n' + orcUrl + '\n\nDepois volta a guardar o orçamento.');
        }
      }
    }

    briefingRef.current = null;
    setSaving(false); await load(); if(!abriuPosGanho) setView('list');
  };

  if(view==='vendas'){
    return <VendasView perms={perms} userEmail={userEmail} voltar={()=>setView('list')}/>;
  }
  if(view==='objetivos'){
    return <ObjetivosView voltar={()=>setView('list')}/>;
  }
  if(view==='dashboard'){
    return <DashboardView perms={perms} userEmail={userEmail} voltar={()=>setView('list')}/>;
  }
  if(view==='comissoes'){
    return <CommissionsView perms={perms} userName={userName} userEmail={userEmail} voltar={()=>setView('list')}/>;
  }
  if(view==='marketing'){
    return <MarketingPage perms={perms} userEmail={userEmail} voltar={vistaInicial==='marketing'?undefined:()=>setView('list')}/>;
  }

  if(view==='list'){
    const card = (label,val,cor,icon)=>(
      <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:'16px 18px', flex:1, minWidth:150 }}>
        <div style={{ fontSize:12, color:C.textLight, marginBottom:6, fontWeight:600 }}>{icon} {label}</div>
        <div style={{ fontSize:24, fontWeight:700, color:cor, fontFamily:"'Fraunces',serif" }}>{val}</div>
      </div>
    );
    return (
      <div style={{ fontFamily:"'DM Sans',sans-serif", padding:24 }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fraunces:wght@700&display=swap" rel="stylesheet"/>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, flexWrap:'wrap', gap:10 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:26, color:C.text, margin:0 }}>Comercial · Orçamentos</h1>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setView('comissoes')} style={{ background:C.purpleLight, border:"1px solid "+C.purple, borderRadius:8, color:C.purple, padding:'9px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>💶 Comissões</button>
            <button onClick={()=>setView('vendas')} style={{ background:C.purpleLight, border:"1px solid "+C.purple, borderRadius:8, color:C.purple, padding:'9px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>📒 Vendas</button>
            <button onClick={()=>setView('objetivos')} style={{ background:C.purpleLight, border:"1px solid "+C.purple, borderRadius:8, color:C.purple, padding:'9px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>🎯 Objetivos</button>
            <button onClick={()=>setView('dashboard')} style={{ background:C.purple, border:"1px solid "+C.purple, borderRadius:8, color:'#fff', padding:'9px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>📊 Dashboard</button>
            <button onClick={abrirNovo} style={{ background:C.pink, border:'none', borderRadius:8, color:'white', padding:'9px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>+ Novo orçamento</button>
          </div>
        </div>
        <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
          {card("Pipeline (s/IVA)", eur(kpis.pipeline), C.purple, "📊")}
          {card("Adjudicado", eur(kpis.adjudicado), C.green, "✅")}
          {card("Margem média", pct(kpis.margemMedia), margemCor(kpis.margemMedia), "📈")}
          {card("Taxa conversão", pct(kpis.conversao), C.blue, "🎯")}
          {card("Pendentes", kpis.pendentes, C.amber, "⏳")}
        </div>
        <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Procurar cliente ou nº..."
            style={{ padding:"7px 11px", border:"1px solid "+(busca?C.purple:C.border), borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:220 }}/>
          <select value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)} style={{ padding:"7px 11px", border:"1px solid "+(filtroEstado?C.purple:C.border), borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none" }}>
            <option value="">Todos os estados</option>
            {Object.entries(ESTADOS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filtroLinha} onChange={e=>setFiltroLinha(e.target.value)} style={{ padding:"7px 11px", border:"1px solid "+(filtroLinha?C.purple:C.border), borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none" }}>
            <option value="">Todas as linhas</option>
            {LINHAS.map(l=><option key={l} value={l}>{l}</option>)}
          </select>
          {perms?.canViewAllCommercial && (
          <select value={filtroComercial} onChange={e=>setFiltroComercial(e.target.value)} style={{ padding:"7px 11px", border:"1px solid "+(filtroComercial?C.purple:C.border), borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none" }}>
            <option value="">Todos os comerciais</option>
            {comerciais.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          )}
          <select value={filtroPeriodo} onChange={e=>setFiltroPeriodo(e.target.value)} title="Filtra pela data de ganho" style={{ padding:"7px 11px", border:"1px solid "+(filtroPeriodo!=="tudo"?C.purple:C.border), borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none" }}>
            <option value="tudo">Todo o período</option>
            <option value="mes">Fechado este mês</option>
            <option value="trimestre">Fechado este trimestre</option>
            <option value="ano">Fechado este ano</option>
          </select>
          {(filtroEstado||filtroLinha||filtroComercial||busca||filtroPeriodo!=="tudo")&&<button onClick={()=>{setFiltroEstado("");setFiltroLinha("");setFiltroComercial("");setBusca("");setFiltroPeriodo("tudo");}} style={{ background:"none", border:"none", color:C.textLight, fontSize:12, cursor:"pointer", textDecoration:"underline", fontFamily:"'DM Sans',sans-serif" }}>Limpar</button>}
          <span style={{ marginLeft:'auto', fontSize:12, color:C.textLight }}>{filtrados.length} orçamento{filtrados.length!==1?'s':''}</span>
        </div>
        <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ background:C.bg }}>
                {["Registo","Nº","Cliente","Linha","Comercial","Valor s/IVA","Margem","Estado","Fecho"].map(h=>
                  <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:700, color:C.textLight, textAlign:'left', textTransform:'uppercase', letterSpacing:0.5, borderBottom:"1px solid "+C.border, whiteSpace:'nowrap' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:C.textLight }}>A carregar...</td></tr>}
                {!loading && filtrados.length===0 && <tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:C.textLight }}>Sem orçamentos. Clica em "+ Novo orçamento" para começar.</td></tr>}
                {!loading && filtrados.map(o=>{
                  const est = ESTADOS[o.estado]||ESTADOS.rascunho;
                  return (
                    <tr key={o.id} onClick={()=>abrirEdicao(o)} style={{ borderBottom:"1px solid "+C.border, cursor:'pointer' }}>
                      <td style={{ padding:'10px 14px', color:C.textMid, whiteSpace:'nowrap' }}>{o.created_at?new Date(o.created_at).toLocaleDateString("pt-PT"):'—'}</td>
                      <td style={{ padding:'10px 14px', fontWeight:600, color:C.text, whiteSpace:'nowrap' }}>{o.numero||'—'}</td>
                      <td style={{ padding:'10px 14px', color:C.text }}>{o.cliente||'—'}</td>
                      <td style={{ padding:'10px 14px', color:C.textMid }}>{linhasDe(o).join(' · ')||'—'}</td>
                      <td style={{ padding:'10px 14px', color:C.textMid }}>{o.comercial_nome||'—'}</td>
                      <td style={{ padding:'10px 14px', fontWeight:700, color:C.text, whiteSpace:'nowrap' }}>{eur(o.calc.semIva)}</td>
                      <td style={{ padding:'10px 14px', fontWeight:700, color:margemCor(o.calc.margemPct), whiteSpace:'nowrap' }}>{pct(o.calc.margemPct)}</td>
                      <td style={{ padding:'10px 14px' }}><span style={{ background:est.bg, color:est.color, borderRadius:6, padding:'3px 9px', fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{est.label}</span></td>
                      <td style={{ padding:'10px 14px', color:o.data_decisao?C.text:C.textLight, fontWeight:o.data_decisao?600:400, whiteSpace:'nowrap' }}>{o.data_decisao?new Date(o.data_decisao).toLocaleDateString("pt-PT"):'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const inp = { padding:"8px 10px", border:"1px solid "+C.border, borderRadius:7, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:11, color:C.textLight, fontWeight:600, marginBottom:4, display:'block', textTransform:'uppercase', letterSpacing:0.4 };
  const set = (patch)=>setDraft(d=>({...d,...patch}));
  const alertas = [];
  draftItens.forEach((it,i)=>{ if((it.desconto_pct||0)>20) alertas.push({cor:C.red, txt:'Item '+(i+1)+': desconto '+it.desconto_pct+'% exige aprovação da Direção'}); else if((it.desconto_pct||0)>10) alertas.push({cor:C.amber, txt:'Item '+(i+1)+': desconto '+it.desconto_pct+'% exige justificação'}); });
  if(calc.semIva>0 && calc.margemPct<MARGEM_CRIT) alertas.push({cor:C.red, txt:'Margem '+calc.margemPct.toFixed(0)+'% é crítica (<30%) — requer aprovação da Direção'});
  else if(calc.semIva>0 && calc.margemPct<MARGEM_MIN) alertas.push({cor:C.amber, txt:'Margem '+calc.margemPct.toFixed(0)+'% abaixo do mínimo (35%)'});

  // Janela que aparece depois de adjudicar. Dois botões que dizem o que fazem.
  // Escrita aqui e não como componente à parte: um componente definido dentro
  // de outro é recriado a cada tecla e o campo do email perderia o foco.
  const janelaPosGanho = (() => {
    if (!posGanho) return null;
    const g = posGanho;
    const fechar = () => { setPosGanho(null); setEmailForm(''); setView('list'); };
    return (
      <div onClick={fechar} style={{ position:'fixed', inset:0, background:'rgba(26,26,46,.45)',
        display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
        <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, maxWidth:520,
          width:'100%', boxShadow:'0 18px 50px rgba(0,0,0,.2)', overflow:'hidden' }}>

          <div style={{ background:C.purple, padding:'16px 22px' }}>
            <div style={{ color:'rgba(255,255,255,.75)', fontSize:12 }}>Orçamento {g.numero} adjudicado</div>
            <div style={{ color:'#fff', fontSize:17, fontWeight:700, marginTop:2 }}>{g.projeto}</div>
          </div>

          <div style={{ padding:'18px 22px' }}>
            <table style={{ width:'100%', fontSize:13, color:C.textMid, borderCollapse:'collapse' }}>
              <tbody>
                <tr><td style={{ padding:'3px 10px 3px 0', color:C.textLight }}>Cliente</td><td>{g.cliente}</td></tr>
                <tr><td style={{ padding:'3px 10px 3px 0', color:C.textLight }}>Valor</td>
                    <td><strong>{g.valor.toLocaleString('pt-PT',{minimumFractionDigits:2})}€</strong> <span style={{ color:C.textLight }}>s/IVA</span></td></tr>
                <tr><td style={{ padding:'3px 10px 3px 0', color:C.textLight }}>Faturação</td><td>{g.modelo}</td></tr>
                {g.fases>0 && <tr><td style={{ padding:'3px 10px 3px 0', color:C.textLight }}>Fases</td><td>{g.fases} criadas a partir do modelo</td></tr>}
                {g.zoho && <tr><td style={{ padding:'3px 10px 3px 0', color:C.textLight }}>Zoho</td><td>sincronizado</td></tr>}
              </tbody>
            </table>

            <div style={{ borderTop:'1px solid '+C.border, margin:'16px 0 0', paddingTop:15 }}>
              {g.cliente_id ? (
                <>
                  <div style={{ fontSize:13, color:C.text, marginBottom:7 }}>
                    Pedir ao cliente os dados de faturação — NIF, morada, email de faturação e contactos.
                  </div>
                  <input value={emailForm} onChange={e=>setEmailForm(e.target.value)} type="email"
                    placeholder="email do contacto do cliente"
                    style={{ width:'100%', padding:'9px 11px', border:'1px solid '+C.border, borderRadius:8,
                      fontSize:13, fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box' }}/>
                  {!g.email && <div style={{ fontSize:11.5, color:C.textLight, marginTop:5 }}>
                    O orçamento não tinha email — indica para quem deve ser enviado.</div>}
                </>
              ) : (
                <div style={{ fontSize:12.5, color:'#8A6D00', background:'#FDF8E7', borderRadius:8, padding:'10px 13px', lineHeight:1.55 }}>
                  Este orçamento não está ligado a um cliente do HUB, por isso não é possível pedir os dados agora.
                  Liga-o e usa depois o botão na ficha do cliente.
                </div>
              )}
            </div>
          </div>

          <div style={{ display:'flex', gap:9, justifyContent:'flex-end', padding:'0 22px 20px', flexWrap:'wrap' }}>
            <button onClick={fechar} disabled={aEnviarForm}
              style={{ background:C.bg, border:'1px solid '+C.border, color:C.textMid, borderRadius:9,
                padding:'10px 16px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>
              Criar projeto apenas</button>
            {g.cliente_id && (
              <button disabled={aEnviarForm || !emailForm.trim()}
                onClick={async ()=>{ setAEnviarForm(true);
                  const ok = await pedirDadosFaturacao(g, emailForm);
                  setAEnviarForm(false);
                  if (ok) { alert(`Pedido enviado para ${emailForm.trim()}.`); fechar(); } }}
                style={{ background:C.purple, border:'none', color:'#fff', borderRadius:9,
                  padding:'10px 16px', fontSize:13, fontWeight:700,
                  cursor: (aEnviarForm||!emailForm.trim()) ? 'default':'pointer',
                  opacity: (aEnviarForm||!emailForm.trim()) ? .55:1, fontFamily:"'DM Sans',sans-serif" }}>
                {aEnviarForm ? 'A enviar…' : 'Criar projeto e pedir dados de faturação'}</button>
            )}
          </div>
        </div>
      </div>
    );
  })();

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", padding:24 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fraunces:wght@700&display=swap" rel="stylesheet"/>
      {janelaPosGanho}
      {briefingAberto && (() => {
        const b = briefingAberto, set = (k, v) => setBriefingAberto({ ...b, [k]: v });
        const obrig = b.renovacao ? ['contexto'] : ['contexto', 'contactos', 'datas_chave'];
        const falta = obrig.filter(k => !String(b[k] || '').trim());
        const campo = (k, t, ajuda, rows = 2) => (
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:12, fontWeight:600, color:C.textMid, display:'block', marginBottom:3 }}>{t}{obrig.includes(k) ? ' *' : ''}</label>
            <textarea rows={rows} value={b[k] || ''} onChange={e=>set(k, e.target.value)} placeholder={ajuda}
              style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid '+C.border, borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", resize:'vertical' }}/>
          </div>);
        return (
          <div onClick={()=>setBriefingAberto(null)} style={{ position:'fixed', inset:0, background:'rgba(26,26,46,.45)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'40px 16px', overflowY:'auto' }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, maxWidth:560, width:'100%', overflow:'hidden', boxShadow:'0 18px 50px rgba(0,0,0,.2)' }}>
              <div style={{ background:C.purple, padding:'14px 22px' }}>
                <div style={{ color:'rgba(255,255,255,.75)', fontSize:12 }}>Antes de marcar Ganho</div>
                <div style={{ color:'#fff', fontSize:17, fontWeight:700, marginTop:2 }}>Briefing para as Operações</div>
              </div>
              <div style={{ padding:'16px 22px' }}>
                <div style={{ fontSize:12.5, color:C.textMid, marginBottom:12, lineHeight:1.5 }}>
                  O projeto vai para o TL da área, que nomeia quem fica com ele. Até as Operações aceitarem, o projeto continua contigo e fica bloqueado.
                  {b.renovacao ? ' Como é uma renovação, basta dizer o que muda.' : ''}
                </div>
                {campo('contexto', b.renovacao ? 'O que muda face ao contrato anterior' : 'Contexto do cliente e o que foi vendido', b.renovacao ? 'Ex.: mais 2 sessões por mês; novo local em Braga' : 'Quem é o cliente, porque comprou, o que espera, particularidades', 3)}
                {campo('contactos', 'Contactos do cliente', 'Nome, função, email, telefone de quem acompanha do lado do cliente')}
                {campo('acessos', 'Acessos', 'Plataformas, logins, morada e acesso ao local, credenciação, IT…')}
                {campo('datas_chave', 'Datas-chave', 'Arranque, eventos, entregas, prazos do cliente')}
                {campo('notas', 'Notas internas', 'Fica também nas Notas internas do projeto', 3)}
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end', alignItems:'center' }}>
                  {falta.length > 0 && <span style={{ fontSize:11.5, color:C.textLight, flex:1 }}>Falta preencher os campos com *</span>}
                  <button onClick={()=>setBriefingAberto(null)} style={{ background:'#F0F0F5', color:C.textMid, border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Cancelar</button>
                  <button disabled={falta.length > 0} onClick={()=>{ const { renovacao, ...resto } = b; briefingRef.current = { ...resto, renovacao: !!renovacao, por: draft && draft.comercial_nome, em: new Date().toISOString() }; setBriefingAberto(null); guardar(); }}
                    style={{ background:C.purple, color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:600, cursor:falta.length?'default':'pointer', opacity:falta.length?0.5:1, fontFamily:"'DM Sans',sans-serif" }}>Marcar ganho e passar às Operações</button>
                </div>
              </div>
            </div>
          </div>);
      })()}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, gap:10, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={()=>setView('list')} style={{ background:C.bg, border:"1px solid "+C.border, borderRadius:8, color:C.textMid, padding:'7px 12px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>← Voltar</button>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:C.text, margin:0 }}>{draft.id?'Editar':'Novo'} orçamento · {draft.numero||'nº automático'}</h1>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {draft.id && <button onClick={apagar} disabled={saving} style={{ background:C.redLight, border:"1px solid "+C.red, borderRadius:8, color:C.red, padding:'9px 16px', fontSize:13, cursor:saving?'wait':'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700, opacity:saving?0.6:1 }}>Apagar</button>}
          <input value={draft.zoho_deal_id||''} placeholder="Link ou ID do negócio Zoho"
            onChange={e=>{ set({zoho_deal_id: e.target.value||null}); }}
            style={{ border:'1px solid '+(draft.zoho_deal_id?C.purple:C.border), borderRadius:8, padding:'9px 12px', fontSize:12, fontFamily:"'DM Sans',sans-serif", width:200, color:C.text }}/>
          <button onClick={()=>{ if(!draft.id){alert('Guarda o orçamento primeiro.');return;} navigator.clipboard.writeText('https://workwell-app.vercel.app/?orc='+draft.id).then(()=>alert('Link copiado! Cola no Deal do Zoho CRM.')); }} style={{ background:C.bg, border:'1px solid '+C.purple, borderRadius:8, color:C.purple, padding:'9px 14px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:600, marginRight:8 }}>🔗 Copiar link</button>
          <button onClick={guardar} disabled={saving} style={{ background:C.purple, border:'none', borderRadius:8, color:'white', padding:'9px 18px', fontSize:13, cursor:saving?'wait':'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700, opacity:saving?0.6:1 }}>{saving?'A guardar...':'Guardar'}</button>
        </div>
      </div>
      <div style={{ display:'flex', gap:18, alignItems:'flex-start', flexWrap:'wrap' }}>
        <div style={{ flex:'1 1 560px', minWidth:320, display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:18 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:14 }}>Dados do cliente</div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={lbl}>Cliente *</label>
                <select style={inp} value={draft.cliente_id||''} onChange={e=>{ const id=e.target.value; const c=clientesN.find(x=>x.id===id); set({cliente_id:id||null, cliente:c?c.nome:''}); }}>
                  <option value="">— escolher cliente —</option>
                  {clientesN.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                <div style={{ display:'flex', gap:6, marginTop:6 }}>
                  <input style={{...inp, flex:1}} placeholder="+ novo cliente (nome)" value={novoCliente} onChange={e=>setNovoCliente(e.target.value)}/>
                  <button type="button" onClick={async()=>{ const nome=novoCliente.trim(); if(!nome) return; const existe = await encontrarCliente(supabase, nome); if(existe && window.confirm(`«${nome}» já pertence ao cliente ${existe.nome}${existe.via && existe.via!=='nome' ? ' (' + existe.via + ')' : ''}.\n\nUsar ${existe.nome} em vez de criar um cliente novo?`)){ set({cliente_id:existe.id, cliente:existe.nome}); setNovoCliente(''); return; } const { data, error } = await supabase.from('clientes').insert({nome}).select('id,nome').single(); if(error){ alert('Erro: '+error.message); return; } setClientesN(p=>[...p,data].sort((a,b)=>a.nome.localeCompare(b.nome,'pt'))); set({cliente_id:data.id, cliente:data.nome}); setNovoCliente(''); }} style={{ background:C.purple, color:'#fff', border:'none', borderRadius:7, padding:'0 14px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Criar</button>
                </div>
                {draft.cliente && !draft.cliente_id && <div style={{ fontSize:11, color:'#B0322B', marginTop:4 }}>Cliente "{draft.cliente}" sem ficha — escolhe da lista ou cria.</div>}
              </div>
              <div><label style={lbl}>NIF</label><input style={inp} value={draft.nif||''} onChange={e=>set({nif:e.target.value})}/></div>
              <div><label style={lbl}>Nº colab.</label><input style={inp} type="number" value={draft.n_colaboradores||''} onChange={e=>set({n_colaboradores:e.target.value})}/></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={lbl}>Contacto</label><input style={inp} value={draft.contacto||''} onChange={e=>set({contacto:e.target.value})}/></div>
              <div><label style={lbl}>Email</label><input style={inp} value={draft.email||''} onChange={e=>set({email:e.target.value})}/></div>
              <div><label style={lbl}>Telefone</label><input style={inp} value={draft.telefone||''} onChange={e=>set({telefone:e.target.value})}/></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
              <div><label style={lbl}>Linhas</label>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                  {LINHAS.map(l=>{ const arr=linhasDe(draft); const on=arr.includes(l); return (
                    <button key={l} type="button" onClick={()=>{ const n=on?arr.filter(x=>x!==l):[...arr,l]; set({linhas:n, linha:n[0]||''}); }}
                      style={{ padding:'6px 10px', borderRadius:14, fontSize:12, cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
                        border:'1px solid '+(on?C.purple:C.border), background:on?C.purpleLight:'#fff', color:on?C.purple:C.textMid, fontWeight:on?700:400 }}>
                      {on?'✓ ':''}{l}</button>); })}
                </div>
                <div style={{ fontSize:10.5, color:C.textLight, marginTop:3 }}>Acrescentam-se sozinhas ao escolher os serviços.</div>
              </div>
              <div><label style={lbl}>Comercial</label>{perms?.canManageUsers ? (
                <select style={inp} value={draft.comercial_email||''} onChange={e=>{ const lst=[{n:'Joana Sousa',m:'joana.sousa@workwell.pt'},{n:'Patrícia Silva',m:'patricia.silva@workwell.pt'},{n:'Sara Batista',m:'sara.batista@workwell.pt'},{n:'Tânia Cunha',m:'tania.cunha@workwell.pt'},{n:'Tiago Santos',m:'tiago.santos@workwell.pt'}]; const sel=lst.find(x=>x.m===e.target.value); set({comercial_email:e.target.value, comercial_nome:sel?sel.n:''}); }}>
                  <option value="">— escolher comercial —</option>
                  {[{n:'Joana Sousa',m:'joana.sousa@workwell.pt'},{n:'Patrícia Silva',m:'patricia.silva@workwell.pt'},{n:'Sara Batista',m:'sara.batista@workwell.pt'},{n:'Tânia Cunha',m:'tania.cunha@workwell.pt'},{n:'Tiago Santos',m:'tiago.santos@workwell.pt'}].map(x=><option key={x.m} value={x.m}>{x.n}</option>)}
                </select>
              ) : (
                <input style={inp} value={draft.comercial_nome||''} onChange={e=>set({comercial_nome:e.target.value})}/>
              )}</div>
              <div><label style={lbl}>Comercial origem</label><input style={inp} value={draft.comercial_origem||''} onChange={e=>set({comercial_origem:e.target.value})} placeholder="Quem originou (se diferente)"/></div>
              <div><label style={lbl}>Mes da venda</label><input style={inp} type="month" value={draft.mes_venda||''} onChange={e=>set({mes_venda:e.target.value})}/></div>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={lbl}>Projeto associado</label>
                <select style={inp} value={draft.projeto_id||''} onChange={e=>set({projeto_id:e.target.value||null})}>
                  <option value="">— sem projeto (cria automático ao ganhar) —</option>
                  {(() => {
                    const doCliente = projetosList.filter(p => (draft.cliente_id && p.cliente_id===draft.cliente_id) || (draft.cliente && (p.client||'').toLowerCase()===(draft.cliente||'').toLowerCase()));
                    const outros = projetosList.filter(p => !doCliente.includes(p));
                    return (<>
                      {doCliente.length>0 && <optgroup label="Deste cliente">{doCliente.map(p=><option key={p.id} value={p.id}>{p.name}{p.status?` · ${p.status}`:''}</option>)}</optgroup>}
                      <optgroup label="Todos os projetos">{outros.map(p=><option key={p.id} value={p.id}>{p.name}{p.client?` (${p.client})`:''}</option>)}</optgroup>
                    </>);
                  })()}
                </select>
                {draft.projeto_id && <div style={{ fontSize:11, color:C.textLight, marginTop:4 }}>Ligado a: {(projetosList.find(p=>p.id===draft.projeto_id)||{}).name || draft.projeto_id}</div>}
              </div>
              <div><label style={lbl}>Equipa envolvida</label><input style={inp} value={draft.equipa_envolvida||''} onChange={e=>set({equipa_envolvida:e.target.value})} placeholder="Nomes da entrega (opcional)"/></div>
              <div style={{ position:'relative' }}>
                <label style={lbl}>Veio de uma lead?</label>
                {draft.lead_id ? (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ ...inp, display:'flex', alignItems:'center', background:C.purpleLight, borderColor:C.purple, color:C.purpleDark, fontWeight:600 }}>
                      {(()=>{ const l=leadsList.find(x=>x.id===draft.lead_id); return l ? ((l.empresa||l.pagina||'lead')+' · '+(l.data||'').slice(0,10)) : 'lead selecionada'; })()}
                    </div>
                    <button type="button" onClick={()=>{ set({lead_id:null}); setBuscaLead(''); }} style={{ background:C.redLight, border:"1px solid "+C.red, borderRadius:8, color:C.red, padding:'8px 10px', cursor:'pointer' }}>✕</button>
                  </div>
                ) : (
                  <>
                    <input style={inp} placeholder="Escreve o nome da empresa…" value={buscaLead} onChange={e=>setBuscaLead(e.target.value)} />
                    {buscaLead.trim().length>=2 && (
                      <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:50, background:C.white, border:"1px solid "+C.border, borderRadius:8, marginTop:4, maxHeight:220, overflowY:'auto', boxShadow:'0 6px 20px rgba(26,26,46,.12)' }}>
                        {leadsList.filter(l=>{ const q=buscaLead.trim().toLowerCase(); return (l.empresa||'').toLowerCase().includes(q) || (l.pagina||'').toLowerCase().includes(q); }).slice(0,30).map(l=>(
                          <div key={l.id} onClick={()=>{ set({lead_id:l.id}); setBuscaLead(''); }} style={{ padding:'8px 11px', cursor:'pointer', fontSize:13, borderBottom:"1px solid "+C.bg }} onMouseDown={e=>e.preventDefault()}>
                            <span style={{ fontWeight:600 }}>{l.empresa||l.pagina||'lead'}</span> <span style={{ color:C.textLight }}>· {(l.data||'').slice(0,10)}</span>
                          </div>
                        ))}
                        {leadsList.filter(l=>{ const q=buscaLead.trim().toLowerCase(); return (l.empresa||'').toLowerCase().includes(q) || (l.pagina||'').toLowerCase().includes(q); }).length===0 && (
                          <div style={{ padding:'8px 11px', color:C.textLight, fontSize:13 }}>Sem resultados. O orçamento fica sem lead.</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div><label style={lbl}>Validade</label><input style={inp} type="date" value={draft.validade||''} onChange={e=>set({validade:e.target.value})}/></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginTop:12 }}>
              <div><label style={lbl}>Tipo de negócio</label><select style={inp} value={draft.tipo_negocio||'Novo'} onChange={e=>set({tipo_negocio:e.target.value})}>{TIPOS_NEGOCIO.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
              <div><label style={lbl}>Tipo de faturação</label><select style={inp} value={draft.billing_model||'Único'} onChange={e=>set({billing_model:e.target.value})}>{BILLING_MODELS.map(b=><option key={b} value={b}>{b}</option>)}</select></div>
              <div></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginTop:12 }}>
              <div><label style={lbl}>Início contrato</label><input style={inp} type="date" value={draft.contract_start||''} onChange={e=>set({contract_start:e.target.value})}/></div>
              <div><label style={lbl}>Fim contrato</label><input style={inp} type="date" value={draft.contract_end||''} onChange={e=>set({contract_end:e.target.value})}/></div>
              <div></div>
            </div>
          </div>
          <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:18 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
              <div style={{ fontWeight:700, fontSize:14, color:C.text }}>Itens</div>
              <div style={{ display:'flex', gap:8 }}>
                <select value="" onChange={e=>{ if(e.target.value){ addServico(e.target.value); e.target.value=''; } }} style={{ ...inp, width:'auto', cursor:'pointer' }}>
                  <option value="">+ Da biblioteca...</option>
                  {LINHAS.map(linha=>(<optgroup key={linha} label={linha}>{servicos.filter(s=>s.linha===linha).map(s=><option key={s.id} value={s.id}>{s.codigo} · {s.nome} ({eur(s.preco_base)})</option>)}</optgroup>))}
                </select>
                <button onClick={addLivre} style={{ background:C.purpleLight, border:"1px solid "+C.purple, borderRadius:7, color:C.purple, padding:'8px 12px', fontSize:12, cursor:'pointer', fontWeight:600, whiteSpace:'nowrap' }}>+ Linha livre</button>
              </div>
            </div>
            {draftItens.length===0 && <div style={{ padding:24, textAlign:'center', color:C.textLight, fontSize:13, background:C.bg, borderRadius:8 }}>Sem itens. Adiciona da biblioteca ou cria uma linha livre.</div>}
            {draftItens.map((it,idx)=>{
              const precoFinal=(it.preco_unit||0)*(1-(it.desconto_pct||0)/100);
              const sub=precoFinal*(it.qtd||0);
              const margItem = sub>0 ? ((sub-(it.custo_unit||0)*(it.qtd||0))/sub)*100 : 0;
              return (
                <div key={it._tmp||it.id||idx} style={{ border:"1px solid "+C.border, borderRadius:9, padding:12, marginBottom:10 }}>
                  <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                    <input style={{ ...inp, flex:1 }} placeholder="Descrição do serviço" value={it.descricao} onChange={e=>updItem(idx,{descricao:e.target.value})}/>
                    <button onClick={()=>delItem(idx)} style={{ background:C.redLight, border:"1px solid "+C.red, borderRadius:7, color:C.red, padding:'0 12px', fontSize:16, cursor:'pointer', fontWeight:700 }}>×</button>
                  </div>
                  {(() => {
                    // serviço da biblioteca (código de faturação) + tipo de faturação deste item
                    const sv = servicos.find(x => x.id === it.servico_id);
                    return (
                      <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap', alignItems:'center' }}>
                        {it.servico_id ? (
                          <span style={{ background:C.purpleLight, color:C.purple, borderRadius:12, padding:'4px 10px', fontSize:11.5, fontWeight:700, display:'inline-flex', gap:6, alignItems:'center' }}>
                            {sv ? `${sv.codigo} · ${sv.nome} · ${sv.linha}` : 'serviço descontinuado'}
                            <button type="button" title="Trocar o serviço" onClick={()=>updItem(idx,{servico_id:null})}
                              style={{ border:'none', background:'transparent', color:C.purple, cursor:'pointer', fontSize:12, padding:0 }}>✎</button>
                          </span>
                        ) : (
                          <select value="" onChange={e=>{ if(e.target.value){ updItem(idx,{servico_id:e.target.value}); const sv2=servicos.find(x=>x.id===e.target.value); if(sv2) juntarLinha(sv2.linha); } }}
                            style={{ ...inp, width:'auto', flex:'1 1 240px', border:'1px solid '+C.red, color:C.red }}>
                            <option value="">Escolher o serviço da biblioteca *</option>
                            {LINHAS.map(linha=>(<optgroup key={linha} label={linha}>{servicos.filter(s=>s.linha===linha).map(s=><option key={s.id} value={s.id}>{s.codigo} · {s.nome}</option>)}</optgroup>))}
                          </select>
                        )}
                        <select value={it.billing_model||''} onChange={e=>updItem(idx,{billing_model:e.target.value||null})}
                          title="Tipo de faturação deste serviço" style={{ ...inp, width:'auto', fontSize:12 }}>
                          <option value="">Faturação: {draft.billing_model||'Único'} (do orçamento)</option>
                          {BILLING_MODELS.map(b=><option key={b} value={b}>Faturação: {b}</option>)}
                        </select>
                      </div>
                    );
                  })()}
                  {(() => {
                    // Care 3 · Care 6 · Flex: preço pela calculadora WoW (igual à folha de Excel)
                    const sv = servicos.find(x => x.id === it.servico_id);
                    const plano = sv && PLANO_POR_CODIGO[sv.codigo];
                    if (!plano) return null;
                    const p = { n: draft.n_colaboradores || '', areas: AREAS_PADRAO, addon: ADDON_AREA, excluiPsicologia: false, minimo: MINIMO_MENSAL, meses: 12, ...(it.calculo || {}), plano };
                    const r = calcularWow(p);
                    const aplicar = (mud) => {
                      const np = { ...p, ...mud };
                      const rr = calcularWow(np);
                      const patch = { calculo: np };
                      if (rr) Object.assign(patch, { preco_unit: Math.round(rr.totalMensal * 100) / 100, custo_unit: Math.round(rr.custoMensal * 100) / 100, qtd: Number(np.meses) || 12, unidade: 'mês' });
                      updItem(idx, patch);
                    };
                    const pct = (v) => (Math.round(v * 1000) / 10).toLocaleString('pt-PT') + '%';
                    const ccy = (v) => v.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                    return (
                      <div style={{ background:C.purpleLight, borderRadius:8, padding:'10px 12px', marginBottom:8 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.purple, marginBottom:8 }}>🧮 Calculadora WoW · {plano}</div>
                        <div style={{ display:'grid', gridTemplateColumns:`repeat(${r && r.flex ? 8 : 7}, minmax(0,1fr))`, gap:8, alignItems:'end' }}>
                          <div><label style={lbl}>Nº colaboradores</label><input style={inp} type="number" value={p.n} onChange={e=>aplicar({ n:e.target.value })}/></div>
                          {r && r.flex && <div><label style={lbl}>Valor médio consulta (€)</label><input style={{ ...inp, ...(p.valorConsulta!=='' && p.valorConsulta!=null ? { borderColor:C.purple, fontWeight:700 } : {}) }} type="number" step="0.5" placeholder={String(r.valorConsultaTabela)} value={p.valorConsulta ?? ''} onChange={e=>aplicar({ valorConsulta:e.target.value })}/></div>}
                          <div><label style={lbl}>Adesão esperada (%)</label><input style={{ ...inp, ...(p.adesaoPct!=='' && p.adesaoPct!=null ? { borderColor:C.purple, fontWeight:700 } : {}) }} type="number" step="0.5" placeholder={r ? String(Math.round(r.adesaoTabela*1000)/10) : ''} value={p.adesaoPct ?? ''} onChange={e=>aplicar({ adesaoPct:e.target.value })}/></div>
                          <div><label style={lbl}>Áreas incluídas</label><input style={inp} type="number" value={p.areas} onChange={e=>aplicar({ areas:e.target.value })}/></div>
                          <div><label style={lbl}>Add-on/área (€)</label><input style={inp} type="number" step="0.01" value={p.addon} onChange={e=>aplicar({ addon:e.target.value })}/></div>
                          <div><label style={lbl}>Exclui Psicologia?</label><select style={inp} value={p.excluiPsicologia ? 'Sim' : 'Não'} onChange={e=>aplicar({ excluiPsicologia: e.target.value === 'Sim' })}><option>Não</option><option>Sim</option></select></div>
                          <div><label style={lbl}>Mínimo mensal (€)</label><input style={inp} type="number" value={p.minimo} onChange={e=>aplicar({ minimo:e.target.value })}/></div>
                          <div><label style={lbl}>Meses</label><input style={inp} type="number" value={p.meses} onChange={e=>aplicar({ meses:e.target.value })}/></div>
                        </div>
                        {r ? (
                          <div style={{ fontSize:12, color:C.textMid, marginTop:8, lineHeight:1.6 }}>
                            {r.flex && <><b>{r.consumo.toLocaleString('pt-PT')}</b> consultas previstas × <b>{ccy(r.valorConsulta)}</b>{r.valorConsultaManual ? <> (tabela: {ccy(r.valorConsultaTabela)} · <button type="button" onClick={()=>aplicar({ valorConsulta:'' })} style={{ border:'none', background:'none', color:C.purple, fontWeight:700, cursor:'pointer', padding:0, fontSize:12 }}>usar o da tabela</button>)</> : ' (até 100 consultas/ano 60 € · até 500 55 € · a partir de 501 50 €)'}{r.aplicouPisoEscalao ? ' — sobe para o total do escalão anterior' : ''} ÷ 12 meses ÷ {r.n.toLocaleString('pt-PT')} colaboradores<br/></>}
                            {r.flex ? 'Preço base' : 'Preço de tabela'} <b>{ccy(r.tabela)}</b>{r.addOns ? <> + add-ons <b>{ccy(r.addOns)}</b></> : null}{p.excluiPsicologia ? ' × 55% (sem Psicologia)' : ''} = <b>{ccy(r.precoColab)}/colab./mês</b> · total mensal <b>{ccy(r.totalMensal)}</b>{r.aplicouMinimo ? ' (mínimo mensal)' : ''} · total anual <b>{ccy(r.totalAnual)}</b><br/>
                            Custo previsto: adesão <b>{pct(r.adesao)}</b>{r.adesaoManual ? <> (tabela: {pct(r.adesaoTabela)} · <button type="button" onClick={()=>aplicar({ adesaoPct:'' })} style={{ border:'none', background:'none', color:C.purple, fontWeight:700, cursor:'pointer', padding:0, fontSize:12 }}>usar a da tabela</button>)</> : ''} · <b>{r.consumo.toLocaleString('pt-PT')}</b> consultas/ano · <b>{ccy(r.custoAnual)}</b>/ano · margem <b style={{ color: r.margemBaixa ? C.red : C.green }}>{Math.round(r.margem*100)}%</b>
                            {r.flex && r.aplicouMinimo && r.valorConsultaReal && <div style={{ marginTop:4 }}>Com o mínimo mensal, cada consulta prevista sai a <b>{ccy(r.valorConsultaReal)}</b>.</div>}
                            {r.flex && r.valorConsulta < 50 && <div style={{ color:C.red, fontWeight:700, marginTop:4 }}>⚠ Valor médio da consulta abaixo de 50 €.</div>}
                            {r.margemBaixa && <div style={{ color:C.red, fontWeight:700, marginTop:4 }}>⚠ Margem abaixo de 40% com esta adesão: confirmar o preço antes de enviar a proposta.</div>}
                            {(Math.abs((Number(it.preco_unit)||0) - Math.round(r.totalMensal*100)/100) > 0.01) && (
                              <div style={{ color:C.red, marginTop:4 }}>O preço da linha foi alterado à mão e não corresponde à calculadora. <button type="button" onClick={()=>aplicar({})} style={{ border:'none', background:'none', color:C.purple, fontWeight:700, cursor:'pointer', padding:0, fontSize:12 }}>Repor o valor calculado</button></div>)}
                          </div>
                        ) : <div style={{ fontSize:12, color:C.red, marginTop:8 }}>Indica o nº de colaboradores para calcular.</div>}
                      </div>);
                  })()}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr) auto', gap:8, alignItems:'end' }}>
                    <div><label style={lbl}>Qtd</label><input style={inp} type="number" value={it.qtd} onChange={e=>updItem(idx,{qtd:Number(e.target.value)})}/></div>
                    <div><label style={lbl}>Preço un.</label><input style={inp} type="number" value={it.preco_unit} onChange={e=>updItem(idx,{preco_unit:Number(e.target.value)})}/></div>
                    <div><label style={lbl}>Desc. %</label><input style={inp} type="number" value={it.desconto_pct} onChange={e=>updItem(idx,{desconto_pct:Number(e.target.value)})}/></div>
                    <div><label style={lbl}>Custo un.</label><input style={inp} type="number" value={it.custo_unit} onChange={e=>updItem(idx,{custo_unit:Number(e.target.value)})}/></div>
                    <div><label style={lbl}>IVA %</label><input style={inp} type="number" value={it.iva} onChange={e=>updItem(idx,{iva:Number(e.target.value)})}/></div>
                    <div style={{ textAlign:'right', minWidth:90 }}>
                      <div style={{ fontSize:10, color:C.textLight }}>subtotal</div>
                      <div style={{ fontWeight:700, color:C.text, fontSize:14 }}>{eur2(sub)}</div>
                      <div style={{ fontSize:10, color:margemCor(margItem) }}>margem {margItem.toFixed(0)}%</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:18 }}>
            <div onClick={()=>setShowCustos(s=>!s)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
              <div style={{ fontWeight:700, fontSize:14, color:C.text }}>Custos adicionais {calc.custosAdic>0?'· '+eur2(calc.custosAdic):''}</div>
              <span style={{ color:C.textLight }}>{showCustos?'▲':'▼'}</span>
            </div>
            {showCustos && (<div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12, marginTop:14 }}>
              {CUSTOS_ADIC.map(([k,label])=>(<div key={k}><label style={lbl}>{label}</label><input style={inp} type="number" value={draft[k]||0} onChange={e=>set({[k]:Number(e.target.value)})}/></div>))}
            </div>)}
          </div>
          <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:18 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:14 }}>Condições e observações</div>
            <label style={lbl}>Condições (visível ao cliente)</label>
            <textarea style={{ ...inp, minHeight:60, marginBottom:12, resize:'vertical' }} value={draft.condicoes||''} onChange={e=>set({condicoes:e.target.value})}/>
            <label style={lbl}>Observações internas</label>
            <textarea style={{ ...inp, minHeight:50, resize:'vertical' }} value={draft.obs_internas||''} onChange={e=>set({obs_internas:e.target.value})}/>
          </div>
        </div>
        <div style={{ flex:'0 0 300px', position:'sticky', top:16, display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:18 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:14 }}>Resumo financeiro</div>
            {[['Valor s/ IVA',eur2(calc.semIva)],['Desconto',eur2(calc.descTot)],['IVA',eur2(calc.iva)],['Valor c/ IVA',eur2(calc.comIva)],['Custo direto',eur2(calc.custoDir)]].map(([l,v])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'5px 0', color:C.textMid }}><span>{l}</span><span style={{ fontWeight:600, color:C.text }}>{v}</span></div>
            ))}
            <div style={{ borderTop:"1px solid "+C.border, marginTop:10, paddingTop:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, color:C.textMid }}>Margem</span>
                <span style={{ background:margemCor(calc.margemPct)+'22', color:margemCor(calc.margemPct), borderRadius:6, padding:'3px 10px', fontWeight:700, fontSize:13 }}>{calc.margemPct.toFixed(0)}%</span>
              </div>
              <div style={{ textAlign:'right', fontSize:22, fontWeight:700, color:margemCor(calc.margemPct), fontFamily:"'Fraunces',serif", marginTop:4 }}>{eur2(calc.margemEur)}</div>
            </div>
          </div>
          {alertas.length>0 && (<div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {alertas.map((a,i)=>(<div key={i} style={{ background:a.cor+'15', border:"1px solid "+a.cor, borderRadius:8, padding:'8px 12px', fontSize:12, color:a.cor, fontWeight:600 }}>⚠️ {a.txt}</div>))}
          </div>)}
          <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:18 }}>
            <label style={lbl}>Estado</label>
            <select style={inp} value={draft.estado} onChange={e=>set({estado:e.target.value})}>
              {Object.entries(ESTADOS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
